<script lang="ts">
export interface EmojiEntry {
  emoji: string;
  name: string;
  slug: string;
  group: string;
  asset: string;
  /** Artwork viewBox size (the build trims Twemoji's 36×36 board to the glyph). */
  w?: number;
  h?: number;
}

/** Parsed once per page: switching tabs remounts this component but reuses the catalog. */
let catalogPromise: Promise<EmojiEntry[]> | null = null;
function emojiCatalog(publicPath: string): Promise<EmojiEntry[]> {
  catalogPromise ??= fetch(`${publicPath.replace(/\/$/, '')}/assets/emoji.json`, { credentials: 'same-origin' })
    .then((response) => {
      if (!response.ok) throw new Error(`Emoji request failed (${response.status})`);
      return response.json();
    })
    .then((data) => (Array.isArray(data?.emoji) ? data.emoji as EmojiEntry[] : []))
    .catch((err) => {
      catalogPromise = null;
      throw err;
    });
  return catalogPromise;
}
</script>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import type { DesignItem } from '@core/types';
import { t } from './i18n';
import { fetchPublicSettings, type CustomLibraryItem } from './settingsApi';
import { useVirtualGrid } from '@core/virtualGrid';


type DesignerCommands = {
  addItem?: (item: Record<string, unknown> | DesignItem, options?: { placement?: 'print-area-center' }) => void;
  selectItem?: (id: string) => void;
};

const props = defineProps<{
  publicPath: string;
}>();

const loading = ref(false);
const error = ref('');
const emoji = ref<EmojiEntry[]>([]);
const query = ref('');
const activeGroup = ref('All');
const hiddenSlugs = ref<Set<string>>(new Set());
const hiddenGroups = ref<Set<string>>(new Set());
const customEmoji = ref<CustomLibraryItem[]>([]);
const gridHost = ref<HTMLElement | null>(null);

const visibleEmoji = computed(() =>
  emoji.value.filter((entry) => !hiddenSlugs.value.has(entry.slug) && !hiddenGroups.value.has(entry.group)),
);
const groups = computed(() => ['All', ...Array.from(new Set(visibleEmoji.value.map((entry) => entry.group))).sort()]);
const filteredAll = computed(() => {
  const needle = query.value.trim().toLowerCase();
  return visibleEmoji.value
    .filter((entry) => activeGroup.value === 'All' || entry.group === activeGroup.value)
    .filter((entry) => !needle || entry.name.toLowerCase().includes(needle) || entry.emoji.includes(needle));
});
// Only the rows near the viewport are mounted; all 1 870 emoji stay one scroll away.
const virtual = useVirtualGrid({
  host: gridHost,
  count: computed(() => filteredAll.value.length),
  columns: 6,
  gap: 6,
});
const windowed = computed(() => filteredAll.value.slice(virtual.start.value, virtual.end.value));

function nextId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `emoji-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

function commands(): DesignerCommands | null {
  return (window as unknown as { Veyop?: { designer?: { commands?: DesignerCommands } } }).Veyop?.designer?.commands ?? null;
}

async function loadEmoji() {
  if (emoji.value.length || loading.value) return;
  loading.value = true;
  error.value = '';
  try {
    const [list, settings] = await Promise.all([
      emojiCatalog(props.publicPath),
      fetchPublicSettings(),
    ]);
    emoji.value = list;
    hiddenSlugs.value = new Set(settings.hidden_emoji_slugs);
    hiddenGroups.value = new Set(settings.hidden_emoji_groups);
    customEmoji.value = settings.customEmoji;
  } catch (err) {
    error.value = err instanceof Error ? err.message : t('Emoji unavailable');
  } finally {
    loading.value = false;
  }
}

const EMOJI_SIZE = 96;

/** Longest side = EMOJI_SIZE, other side follows the glyph so the box hugs the artwork. */
function emojiBox(entry: EmojiEntry): { width: number; height: number } {
  const w = Number(entry.w) || 1;
  const h = Number(entry.h) || 1;
  const scale = EMOJI_SIZE / Math.max(w, h);
  return { width: Math.max(1, Math.round(w * scale)), height: Math.max(1, Math.round(h * scale)) };
}

function addEmoji(entry: EmojiEntry) {
  const id = nextId();
  const item = {
    id,
    type: 'svg',
    top: 50,
    left: 50,
    ...emojiBox(entry),
    angle: 0,
    zIndex: 0,
    src: `${props.publicPath.replace(/\/$/, '')}/${entry.asset}`,
    fileUrl: `${props.publicPath.replace(/\/$/, '')}/${entry.asset}`,
    change_color: false,
    svgColors: {},
  };
  const cmd = commands();
  cmd?.addItem?.(item, { placement: 'print-area-center' });
}

function addCustomEmoji(entry: CustomLibraryItem) {
  const id = nextId();
  const item: DesignItem = {
    id,
    type: 'image',
    top: 50,
    left: 50,
    width: 96,
    height: 96,
    angle: 0,
    zIndex: 0,
    src: entry.url,
  };
  const cmd = commands();
  cmd?.addItem?.(item, { placement: 'print-area-center' });
}


onMounted(() => {
  void loadEmoji();
});
</script>

<template>
  <section class="vptw:flex vptw:flex-col vptw:gap-2" :aria-label="t('Emoji')">
    <input
      v-model="query"
      type="search"
      class="vptw:w-full! vptw:rounded-lg! vptw:border vptw:border-(--vs-line)! vptw:bg-(--vs-surface)! vptw:px-3! vptw:py-2! vptw:text-xs vptw:text-(--vs-ink)! vptw:shadow-none! vptw:outline-none vptw:focus:border-(--vs-accent)! vptw:focus:ring-2! vptw:focus:ring-(--vs-accent-soft)!"
      :placeholder="t('Search emoji')"
      @focus="void loadEmoji()"
    />

    <div class="vptw:flex vptw:gap-1 vptw:overflow-x-auto vptw:pb-1">
      <button
        v-for="group in groups"
        :key="group"
        type="button"
        class="vptw:shrink-0 vptw:rounded-full! vptw:border! vptw:px-2.5! vptw:py-1! vptw:text-[10px]! vptw:font-medium! vptw:cursor-pointer"
        :class="activeGroup === group ? 'vptw:border-(--vs-accent)! vptw:bg-(--vs-accent-soft)! vptw:text-(--vs-accent)!' : 'vptw:border-(--vs-line)! vptw:bg-(--vs-surface)! vptw:text-(--vs-ink-2)!'"
        @click="activeGroup = group"
      >
        {{ group === 'All' ? t('All') : group }}
      </button>
    </div>

    <p v-if="loading" class="vptw:text-xs vptw:text-(--vs-ink-3)">{{ t('Loading emoji...') }}</p>
    <p v-else-if="error" class="vptw:text-xs vptw:text-red-500">{{ error }}</p>
    <div v-else ref="gridHost" class="vptw:relative" :style="{ height: `${virtual.totalHeight.value}px` }">
      <div
        class="vptw:absolute vptw:inset-x-0 vptw:top-0 vptw:grid vptw:grid-cols-6 vptw:gap-1.5"
        :style="{ transform: `translateY(${virtual.offsetY.value}px)` }"
      >
      <button
        v-for="entry in windowed"
        :key="entry.emoji"
        type="button"
        class="vptw:flex! vptw:aspect-square vptw:items-center vptw:justify-center vptw:rounded-lg! vptw:border! vptw:border-(--vs-line)! vptw:bg-(--vs-surface)! vptw:p-1.5! vptw:transition vptw:hover:border-(--vs-accent)! vptw:hover:bg-(--vs-accent-soft)! vptw:cursor-pointer"
        :title="entry.name"
        :aria-label="`${t('Insert')} ${entry.name}`"
        @click="addEmoji(entry)"
      >
        <img :src="`${publicPath.replace(/\/$/, '')}/${entry.asset}`" :alt="entry.name" class="vptw:size-full vptw:object-contain" decoding="async" />
      </button>
      </div>
    </div>
    <p v-if="!loading && !error && !filteredAll.length" class="vptw:m-0 vptw:text-xs vptw:text-(--vs-ink-3)">{{ t('No emoji found.') }}</p>

    <template v-if="!loading && !error && customEmoji.length">
      <p class="vptw:m-0 vptw:mt-1 vptw:text-[10px] vptw:font-semibold vptw:uppercase vptw:text-(--vs-ink-3)">{{ t('Custom') }}</p>
      <div class="vptw:grid vptw:grid-cols-6 vptw:gap-1.5">
        <button
          v-for="entry in customEmoji"
          :key="entry.id"
          type="button"
          class="vptw:flex! vptw:aspect-square vptw:items-center vptw:justify-center vptw:rounded-lg! vptw:border! vptw:border-(--vs-line)! vptw:bg-(--vs-surface)! vptw:p-1! vptw:transition vptw:hover:border-(--vs-accent)! vptw:hover:bg-(--vs-accent-soft)! vptw:cursor-pointer"
          :title="entry.label"
          :aria-label="`${t('Insert')} ${entry.label}`"
          @click="addCustomEmoji(entry)"
        >
          <img :src="entry.url" :alt="entry.label" class="vptw:size-full vptw:object-contain" loading="lazy" />
        </button>
      </div>
    </template>
  </section>
</template>
